import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function StressPage() {
  const stressSymptomsList = [
    {
      title: "Tankemylder & koncentrationsbesvær",
      text: "Tankemylder og svært ved at koncentrere dig",
      icon: "fa-solid fa-brain",
      detail: "Nervesystemet er i alarmberedskab, hvilket gør det svært at samle tankerne eller finde mental ro i hverdagen.",
    },
    {
      title: "Søvnproblemer & opvågninger",
      text: "Problemer med at falde i søvn eller hyppige opvågninger",
      icon: "fa-solid fa-moon",
      detail: "Uro i kroppen forstyrrer den naturlige indsovning og medfører afbrudt søvn eller tidlig morgenvågning.",
    },
    {
      title: "Muskelspændinger, hovedpine & træthed",
      text: "Muskelspændinger, hovedpine og træthed",
      icon: "fa-solid fa-head-side-virus",
      detail: "Vedvarende muskeltonus i nakke, skuldre og kæber kombineret med en følelse af konstant energiløshed.",
    },
    {
      title: "Kortere lunte & nedsat overskud",
      text: "Kortere lunte og mindre overskud til andre",
      icon: "fa-solid fa-heart-pulse",
      detail: "Mindre tålmodighed og manglende mentalt frirum til relationer og hverdagens almindelige gøremål.",
    },
  ];

  const everydayAdvice = [
    {
      number: "01",
      title: "Tal med nogen om det, der belaster dig",
      desc: "Del dine oplevelser med dine nærmeste, en kollega eller din læge. At sætte ord på belastningen kan lette det indre pres.",
      icon: "fa-solid fa-comments",
    },
    {
      number: "02",
      title: "Skab plads til pauser og færre krav",
      desc: "Giv krop og sind mulighed for at koble fra i løbet af dagen. Skær ned på ikke-nødvendige opgaver og skab små åndehuller.",
      icon: "fa-solid fa-leaf",
    },
    {
      number: "03",
      title: "Søg hjælp tidligt, hvis dit overskud falder",
      desc: "Vent ikke med at række ud, indtil batteriet er helt tømt. Jo tidligere du tager hånd om mistrivsel, jo hurtigere kan balancen genfindes.",
      icon: "fa-solid fa-hand-holding-heart",
    },
  ];

  const treatmentSteps = [
    {
      number: "01",
      title: "Indledende samtale",
      desc: "Vi begynder med en grundig samtale om dine gener, din hverdag, dit søvnmønster og det, du ønsker hjælp til.",
    },
    {
      number: "02",
      title: "Valg af behandlingsform",
      desc: "Vi drøfter dine ønsker og afklarer, om japansk akupunktur, NADA-øreakupunktur eller en kombination er mest relevant.",
    },
    {
      number: "03",
      title: "Nænsom behandling",
      desc: "Behandlingen tilpasses din sensitivitet med ultra-tynde sterile engangsnåle eller ikke-penetrerende kontaktnåle.",
    },
    {
      number: "04",
      title: "Løbende opfølgning",
      desc: "Ved næste besøg følger vi op på din oplevelse og justerer tilgangen efter dine personlige reaktioner og behov.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Akupunktur mod Stress i Risskov | Få Ro</title>
        <meta
          name="description"
          content="Dæmp stress, uro og søvnløshed med blid akupunktur i Risskov nær Aarhus. Skab ro i nervesystemet og genfind balancen i krop og sind. Læs mere her."
        />
        <meta
          property="og:title"
          content="Akupunktur mod Stress i Risskov | Få Ro"
        />
        <meta
          property="og:description"
          content="Dæmp stress, uro og søvnløshed med blid akupunktur i Risskov nær Aarhus. Skab ro i nervesystemet og genfind balancen i krop og sind. Læs mere her."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/stress" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Stress, symptomer og akupunktur for dig fra Risskov, Aarhus og Jystrup",
            "url": "https://kita-akupunktur.dk/stress",
            "description":
              "Fylder tankemylder, uro eller dårlig søvn i din hverdag? Hos Kita Akupunktur i Risskov ved Aarhus tilbyder jeg individuelt tilpasset akupunktur ved stress, også for dig fra Jystrup.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={2} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li>
                <Link to="/akupunktur" className="hover:text-[#d74814] transition-colors">
                  Akupunktur
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Stress
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>FOKUSOMRÅDE • STRESS & NERVESYSTEM</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] mb-6 max-w-4xl leading-tight">
            Stress, symptomer og akupunktur for dig fra Risskov, Aarhus og Jystrup
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] max-w-3xl leading-relaxed mb-8">
            Fylder tankemylder, uro eller dårlig søvn i din hverdag? Hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            i Risskov ved Aarhus tilbyder jeg individuelt tilpasset akupunktur ved stress, også for dig fra Jystrup. Jeg hedder Mayumi Kita Thiele, og hos mig begynder forløbet med en samtale om dine gener, din hverdag og det, du ønsker hjælp til.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-2xl bg-[#d74814] text-white font-medium hover:bg-[#b83b0e] transition-all shadow-md hover:shadow-lg"
            >
              <span>Bestil tid eller stil spørgsmål</span>
              <i className="fa-solid fa-calendar-check ml-2" aria-hidden="true" />
            </Link>
            <a
              href="tel:29387186"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-2xl bg-white border border-[#ffe0cf] text-[#593427] font-medium hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] mr-2" aria-hidden="true" />
              <span>Ring 29 38 71 86</span>
            </a>
          </div>

          <div className="mt-8 pt-6 border-t border-[#ffe0cf]/60 flex flex-wrap items-center gap-6 text-xs sm:text-sm text-[#7a5b4f]">
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-shield-check text-[#d74814]" aria-hidden="true" />
              <span>RAB-registreret akupunktør</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-feather-pointed text-[#d74814]" aria-hidden="true" />
              <span>Nænsom japansk akupunktur</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-ear-listen text-[#d74814]" aria-hidden="true" />
              <span>Certificeret i NADA</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="fa-solid fa-square-parking text-[#d74814]" aria-hidden="true" />
              <span>Gratis parkering ved klinikken</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-16">
        {/* Section 1: Symptomer på stress */}
        <section className="bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-8">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>KROPPENS SIGNALER & REAKTIONER</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Hvilke symptomer på stress skal du være opmærksom på?
            </h2>
            <div className="text-[#7a5b4f] space-y-3 leading-relaxed text-sm sm:text-base">
              <p>
                Stress er en reaktion på belastning. I korte perioder er reaktionen naturlig, men vedvarende pres kan påvirke både kroppen, humøret og dit overskud. Symptomer på stress varierer fra person til person. Det er derfor vigtigt at lægge mærke til ændringer fra det, der er normalt for dig.
              </p>
            </div>
          </div>

          {/* 4 Symptoms Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {stressSymptomsList.map((item, index) => (
              <div
                key={index}
                className="bg-[#fffaf7] p-6 rounded-2xl border border-[#ffe0cf] hover:border-[#d74814]/40 transition-all shadow-2xs flex flex-col justify-between space-y-3"
              >
                <div className="space-y-3">
                  <div className="w-11 h-11 rounded-xl bg-white border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                    <i className={`${item.icon} text-lg`} aria-hidden="true" />
                  </div>
                  <h3 className="font-serif font-semibold text-base text-[#593427] leading-snug">
                    {item.text}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Medical Notice & Neck/Shoulder Callout */}
          <div className="p-6 sm:p-7 rounded-2xl bg-[#FFF5EE] border border-[#ffe0cf] space-y-3">
            <div className="flex items-start space-x-3.5">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#d74814] shrink-0 border border-[#ffe0cf]">
                <i className="fa-solid fa-user-doctor text-base" aria-hidden="true" />
              </div>
              <div className="space-y-2">
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Disse tegn betyder ikke nødvendigvis, at du har stress. De kan også have andre årsager. Kontakt din læge, hvis symptomerne varer ved eller påvirker din hverdag. Oplever du især spændinger i nakke og skulder, kan du også læse om mit tilbud ved disse gener.
                </p>
                <div>
                  <Link
                    to="/spaendinger"
                    className="inline-flex items-center space-x-1.5 text-xs sm:text-sm font-semibold text-[#d74814] hover:text-[#b83b0e]"
                  >
                    <span>Læs mere om mit tilbud ved spændinger i nakke og skuldre</span>
                    <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Akupunktur mod stress & Hvis du er utryg ved nåle */}
        <section className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Card 1: Akupunktur mod stress med fokus på dig */}
            <div className="bg-white/95 p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-xs flex flex-col justify-between space-y-5">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>INDIVIDUEL TILPASNING</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Akupunktur mod stress med fokus på dig
                </h2>
                <div className="text-[#7a5b4f] space-y-3 leading-relaxed text-sm sm:text-base">
                  <p>
                    Jeg tilbyder akupunktur mod stress i Risskov ved Aarhus, også for dig fra Jystrup. I klinikken arbejder jeg især med japansk akupunktur. Her bruger jeg meget tynde, sterile engangsnåle og tilpasser teknikken til din sensitivitet. Jeg er registreret som alternativ behandler, RAB, og har videreuddannet mig inden for japansk akupunktur.
                  </p>
                  <p>
                    Ved den første samtale taler vi om, hvordan du har det, hvor længe dine gener har stået på, og hvordan du sover. Jeg spørger også til din øvrige behandling. Det giver et grundlag for at drøfte, hvad du ønsker af forløbet, og hvilken af klinikkens behandlingsformer der er relevant at vælge.
                  </p>
                </div>
              </div>

              <div className="pt-3 border-t border-[#ffe0cf]/70 flex flex-wrap items-center justify-between gap-3">
                <Link
                  to="/japansk-akupunktur"
                  className="inline-flex items-center space-x-1.5 text-xs sm:text-sm font-semibold text-[#d74814] hover:text-[#b83b0e]"
                >
                  <span>Læs mere om japansk akupunktur</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                </Link>
                <div className="flex items-center space-x-2 text-xs text-[#7a5b4f]">
                  <i className="fa-solid fa-circle-check text-[#829e2f]" aria-hidden="true" />
                  <span>RAB-registreret</span>
                </div>
              </div>
            </div>

            {/* Card 2: Hvis du er utryg ved nåle */}
            <div className="bg-gradient-to-br from-white via-[#fffaf7] to-[#fff5ee] p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-xs flex flex-col justify-between space-y-5">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>NÅLEFØLSOMHED & TRYGHED</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Hvis du er utryg ved nåle
                </h2>
                <div className="text-[#7a5b4f] space-y-3 leading-relaxed text-sm sm:text-base">
                  <p>
                    Du må gerne fortælle mig, hvis du er nervøs for nålene eller let bliver overvældet af berøring. Ved japansk akupunktur kan jeg anvende kontaktnåle, som berører huden uden at trænge ind. Vi aftaler tilgangen sammen, og du kan altid sige til, hvis noget føles ubehageligt under behandlingen.
                  </p>
                  <p>
                    Ved de næste besøg følger vi op på din oplevelse og eventuelle ændringer i dine gener. Behandlingen justeres efter dine reaktioner, og vi vurderer løbende, om forløbet giver mening for dig. Der er ikke et fast antal behandlinger, som passer til alle, og jeg lover ikke et bestemt resultat.
                  </p>
                </div>
              </div>

              <div className="pt-3 border-t border-[#ffe0cf]/70">
                <div className="p-3.5 rounded-xl bg-white border border-[#ffe0cf] flex items-center space-x-3 text-xs sm:text-sm text-[#593427]">
                  <JapaneseBadge className="shrink-0" />
                  <span>Kontaktnåle og nænsom tilgang tilpasset din sensitivitet.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: NADA & Søvnproblemer */}
        <section className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* NADA behandling */}
            <div className="bg-white/95 p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-xs flex flex-col justify-between space-y-5">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>ØREAKUPUNKTUR • NADA</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Behandling med NADA for dig fra Aarhus, Risskov og Jystrup
                </h2>
                <div className="text-[#7a5b4f] space-y-3 leading-relaxed text-sm sm:text-base">
                  <p>
                    NADA er en bestemt form for øreakupunktur med fem små nåle i hvert øre. Under behandlingen sidder du i ro med nålene. Jeg er certificeret i NADA gennem NADA Danmark. Du kan få behandling med NADA her i klinikken efter aftale, og vi taler først om dine gener og dine ønsker til besøget.
                  </p>
                  <p>
                    NADA og japansk akupunktur er forskellige metoder. NADA følger et fast sæt ørepunkter, mens den japanske behandling tilpasses individuelt. Dokumentationen for akupunktur og NADA ved stress er begrænset og usikker. Tilbuddene skal derfor ses som supplement, ikke som erstatning for lægelig eller psykologisk hjælp.
                  </p>
                </div>
              </div>

              <div className="pt-3 border-t border-[#ffe0cf]/70">
                <Link
                  to="/oere-akupunktur"
                  className="inline-flex items-center space-x-1.5 text-xs sm:text-sm font-semibold text-[#d74814] hover:text-[#b83b0e]"
                >
                  <span>Læs mere om øreakupunktur & NADA</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Søvnproblemer */}
            <div className="bg-white/95 p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-xs flex flex-col justify-between space-y-5">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>SØVNKVALITET & RO</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Akupunktur mod søvnproblemer
                </h2>
                <div className="text-[#7a5b4f] space-y-3 leading-relaxed text-sm sm:text-base">
                  <p>
                    Søger du akupunktur mod søvnproblemer i Aarhus eller Risskov, eller kommer du fra Jystrup? Vi kan tale om et forløb i klinikken. Jeg spørger ind til dit søvnmønster og til, om du har svært ved at falde i søvn, vågner om natten eller vågner for tidligt.
                  </p>
                  <p>
                    Søvnproblemer skyldes ikke altid stress, og vedvarende problemer bør vurderes af din læge. Regelmæssige sengetider og mindre koffein sent på dagen er mulige steder at begynde. Et forløb hos mig erstatter ikke udredning eller relevant behandling, og akupunktur giver ikke sikkerhed for bedre søvn.
                  </p>
                </div>
              </div>

              <div className="pt-3 border-t border-[#ffe0cf]/70">
                <div className="flex items-center space-x-2 text-xs text-[#7a5b4f]">
                  <i className="fa-solid fa-moon text-[#d74814]" aria-hidden="true" />
                  <span>Fokus på at skabe rolige forudsætninger for din nattesøvn</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Hjælp til stress handler også om din hverdag */}
        <section className="bg-gradient-to-br from-white via-[#FFF8F4] to-[#FFF5EE] p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden space-y-8">
          <div className="max-w-3xl space-y-3">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>HELHEDSORIENTERET INDSATS</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Hjælp til stress handler også om din hverdag
            </h2>
            <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
              Behandling af stress handler først og fremmest om at få øje på belastningerne og skabe mulighed for at komme sig. Det kan kræve ændringer på arbejdet, hjemme eller begge steder. Din læge kan hjælpe med en vurdering og med at afklare behovet for anden støtte, eksempelvis samtaler hos en psykolog.
            </p>
          </div>

          {/* 3 Everyday Advice Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {everydayAdvice.map((item) => (
              <div
                key={item.number}
                className="bg-white/95 p-6 rounded-2xl border border-[#ffe0cf] shadow-2xs space-y-3"
              >
                <div className="w-10 h-10 rounded-xl bg-[#ffe0cf]/40 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <h3 className="font-serif font-semibold text-base text-[#593427]">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="p-6 rounded-2xl bg-white/80 border border-[#ffe0cf] text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
            <p>
              Du behøver ikke have overblik over det hele, før du søger hjælp til stress. I samtalen hos mig er der plads til at beskrive din situation med dine egne ord. Mit tilbud er akupunktur og NADA som supplement til den øvrige støtte, du har brug for, ikke en løsning på alle årsagerne til din stress.
            </p>
          </div>
        </section>

        {/* Section 5: Sådan forløber et forløb hos mig */}
        <section className="space-y-6">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>BEHANDLINGSFORLØB</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Sådan foregår et forløb hos Kita Akupunktur
            </h2>
            <p className="text-[#7a5b4f] text-sm sm:text-base">
              Hos mig mødes du med ro, nærvær og professionel omsorg i rolige rammer i Risskov.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {treatmentSteps.map((step) => (
              <div
                key={step.number}
                className="bg-white/90 p-6 rounded-2xl border border-[#ffe0cf] shadow-2xs relative space-y-3 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="text-3xl font-serif font-bold text-[#d74814]/30">
                    {step.number}
                  </div>
                  <h3 className="font-serif font-semibold text-lg text-[#593427]">
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6: Skal vi tale om dine muligheder? & Klinikinformation */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-6">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>SAMTALE & TIDSBESTILLING</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Skal vi tale om dine muligheder?
            </h2>
            <div className="text-[#7a5b4f] space-y-4 text-sm sm:text-base leading-relaxed">
              <p>
                Er du i tvivl om forskellen på japansk akupunktur og NADA, eller vil du høre mere om et forløb? Du er velkommen til at spørge inden tidsbestilling. Du kan også læse om min faglige baggrund og få et indtryk af den tilgang, du møder i klinikken.
              </p>
              <p>
                Kita Akupunktur ligger på Kantorparken 11, 8240 Risskov ved Aarhus. Kommer du fra Jystrup, er du også velkommen, og der er gratis parkering ved klinikken. Jeg tilbyder behandling efter aftale. Ring på 29 38 71 86, eller kontakt mig for at stille spørgsmål eller bestille tid. Sammen afklarer vi, hvad du ønsker af dit besøg.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Link
                to="/om-mig"
                className="inline-flex items-center space-x-2 text-xs sm:text-sm font-semibold text-[#d74814] hover:text-[#b83b0e]"
              >
                <span>Læs om min faglige baggrund og tilgang</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 bg-gradient-to-br from-[#FFF5EE] to-[#FFFAF7] p-8 rounded-3xl border border-[#ffe0cf] shadow-sm flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <i className="fa-solid fa-heart text-[#d74814]" aria-hidden="true" />
                <span>RAB REGISTRERET</span>
              </div>
              <h3 className="text-xl font-serif text-[#593427]">
                Tilskud fra Sygeforsikringen "danmark" & sundhedsforsikring
              </h3>
              <p className="text-sm text-[#7a5b4f] leading-relaxed">
                Da jeg er Registreret Alternativ Behandler (RAB), yder Sygeforsikringen "danmark" tilskud til akupunkturbehandling. De fleste private sundhedsforsikringer dækker ligeledes helt eller delvist dit forløb ved en lægelig henvisning eller godkendt dækning.
              </p>
              <div className="pt-2">
                <Link
                  to="/priser"
                  className="inline-flex items-center space-x-2 text-sm font-semibold text-[#d74814] hover:text-[#b83b0e]"
                >
                  <span>Se priser og tilskudsregler her</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="pt-4 border-t border-[#ffe0cf] flex flex-col sm:flex-row sm:items-center justify-between text-xs text-[#7a5b4f] gap-2">
              <div className="flex items-center space-x-2">
                <i className="fa-solid fa-location-dot text-[#d74814]" aria-hidden="true" />
                <span>Kantorparken 11, 8240 Risskov</span>
              </div>
              <a href="tel:29387186" className="text-[#d74814] font-semibold hover:underline flex items-center space-x-1.5">
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Tlf. 29 38 71 86</span>
              </a>
            </div>
          </div>
        </section>

        {/* Related Treatments Grid */}
        <section className="pt-4 border-t border-[#ffe0cf] space-y-4">
          <h3 className="text-xl font-serif text-[#593427]">
            Andre relevante behandlinger:
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/spaendinger"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-head-side-virus text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Spændinger & myoser
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Nakke, skuldre og ryg</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/migraene"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-brain text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Migræne & hovedpine
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Nænsom japansk lindring</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/oere-akupunktur"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-ear-listen text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    NADA & Øreakupunktur
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Ro og afbalancering</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/japansk-akupunktur"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-feather-pointed text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Japansk akupunktur
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Blid Keiraku Chiryo</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/ondt-i-kaeben"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-teeth text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Ondt i kæben
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Kæbespændinger & tandpres</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>

            <Link
              to="/rygestop-behandling"
              className="p-4 rounded-xl glass-pill hover:bg-white transition-all flex items-center justify-between group shadow-xs border border-[#ffe0cf]"
            >
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-ban-smoking text-[#d74814]" aria-hidden="true" />
                <div>
                  <span className="font-medium text-[#593427] block group-hover:text-[#d74814] text-sm">
                    Rygestop behandling
                  </span>
                  <span className="text-xs text-[#7a5b4f]">Ro mod abstinenser</span>
                </div>
              </div>
              <i className="fa-solid fa-arrow-right text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* Hurtige genveje */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-xs sm:text-sm">
          <Link
            to="/priser"
            className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
          >
            Se priser & tilskud fra “danmark”
          </Link>
          <Link
            to="/om-mig"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Læs om Mayumis speciale i stressbehandling
          </Link>
          <Link
            to="/faq"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Samlet FAQ
          </Link>
          <Link
            to="/kontakt"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Bestil tid i Risskov
          </Link>
        </div>

        {/* Contact Form Section */}
        <section className="pt-6">
          <ContactForm
            title="Book tid til stressbehandling eller stil et spørgsmål"
            subtitle="Fortæl gerne kort om dine gener og symptomer. Jeg vender hurtigt tilbage for at finde en tid, der passer dig i rolige rammer i Risskov."
          />
        </section>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om stressbehandling"
            subtitle="Få svar på hvordan nænsom akupunktur dæmper nervesystemets alarmberedskab, forbedrer nattesøvnen og genskaber kropslig ro."
            faqs={[
              {
                question: "Hvordan hjælper akupunktur mod stress og overbelastning af nervesystemet?",
                answer:
                  "Akupunktur stimulerer kroppens parasympatiske nervesystem – også kendt som 'hvil og fordøj'-systemet. Ved at påvirke specifikke beroligende meridianpunkter dæmpes produktionen af stresshormoner som kortisol og adrenalin, mens udskillelsen af gavnlige endorfiner fremmes. Dette sænker hjertefrekvensen, løsner kroniske muskelspændinger og hjælper kroppen ud af den udmattende 'kæmp eller flygt'-tilstand.",
              },
              {
                question: "Kan akupunktur hjælpe på søvnproblemer og tankemylder forårsaget af stress?",
                answer: (
                  <>
                    Ja, mærkbart forbedret søvnkvalitet er ofte en af de allerførste positive virkninger, mine klienter oplever. Langvarig stress skaber hyppigt indre uro og tankemylder, der forhindrer kroppen i at falde i søvn eller medfører brat opvågning om natten. Gennem{" "}
                    <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      japansk akupunktur
                    </Link>{" "}
                    og blid afbalancering af kroppens meridianer dæmpes den mentale overaktivitet, så du lettere glider ind i en dyb, restituerende nattesøvn.
                  </>
                ),
              },
              {
                question: "Hvor mange behandlinger skal der til ved længerevarende stress eller udbrændthed?",
                answer:
                  "Ved længerevarende stress har kroppen typisk været i højeste alarmberedskab gennem mange uger eller måneder, og nervesystemet kræver tid og kontinuitet for at genfinde tryghed. De fleste mærker en umiddelbar lettelse og ro allerede efter første session, men et solidt forløb kræver som regel 5-8 ugentlige sessioner. Vi tilpasser løbende behandlingen efter dine personlige ressourcer og evaluerer fremgangen.",
              },
              {
                question: "Jeg føler mig udmattet og overstimuleret – er behandlingen for voldsom for mig?",
                answer: (
                  <>
                    Tværtimod. Min tilgang med{" "}
                    <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      japansk akupunktur (Keiraku Chiryo)
                    </Link>{" "}
                    er ekstremt nænsom og udviklet specifikt til overbelastede og sensitive kroppe. Vi benytter ultratynde nåle og minimal stimulation, så kroppen ikke overvældes af nye sanseindtryk. Du ligger behageligt og lunt på briksen i et dæmpet, harmonisk behandlingsrum med beroligende musik og god tid til at trække vejret helt i bund.
                  </>
                ),
              },
              {
                question: "Kan jeg kombinere stressbehandlingen med psykologhjælp eller lægelig opfølgning?",
                answer: (
                  <>
                    Ja, akupunktur fungerer ideelt som et kropsligt supplement til samtaleterapi, psykologforløb eller lægelig sygemelding. Hvor samtaleterapi arbejder med tankemønstre og årsager, afhjælper akupunkturen de fysiske reaktioner – såsom hjertebanken, trykken for brystet, mavekramper og anspændthed. Da jeg er{" "}
                    <Link to="/om-mig" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      RAB-godkendt
                    </Link>
                    , yder Sygeforsikring “danmark” desuden tilskud, når der foreligger en lægelig diagnose. Se mere under{" "}
                    <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      priser og tilskud
                    </Link>
                    .
                  </>
                ),
              },
            ]}
          />
        </section>
      </div>
    </>
  );
}
